import React from "react";

import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import {
  Stack,
  IconButton,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useMutation, useQuery } from "@tanstack/react-query";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

import UserRow from "./parts/UserRow";
import {
  OrganizationUser,
  SetUserActivePayload,
} from "../../services/user-management.service";
import { userManagementService } from "../../services";
import { Column } from "../../../common/Table/types";
import TableView from "../../../common/Table";
import { formatDateAndTime } from "../../../../utils/formatDate";
import { RoleName } from "../../../common/enums/RoleName";
import ConfirmUserDeleteDialog from "../ConfirmUserDeleteDialog";
import ManageRolesDialog from "../ManageRolesDialog";
import ButtonView from "../../../common/Button";
import RoleBasedAccess from "../../../common/RoleBasedAccess";

const columns: Column[] = [
  { id: "email", label: "Email", minWidth: 100 },
  { id: "first_name", label: "First Name", minWidth: 100 },
  { id: "last_name", label: "Last Name", minWidth: 100 },
  { id: "is_active", label: "Is Active", minWidth: 100 },
  { id: "roles", label: "Roles", minWidth: 100 },
  { id: "created_at", label: "Created At", minWidth: 100 },
  { id: "delete", label: "Delete", minWidth: 100 },
];

function createData(
  id: string,
  email: string,
  first_name: string,
  last_name: string,
  is_active: boolean,
  created_at: string,
  roles: RoleName[],
): any {
  return {
    id,
    email,
    first_name,
    last_name,
    is_active,
    created_at,
    roles,
  };
}

interface Props {
  handleOpenAddUserDialog: () => void;
}

export default function UsersList({ handleOpenAddUserDialog }: Props) {
  const [page, setPage] = React.useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
  const [isRefreshing, setIsRefreshing] = React.useState<boolean>(false);
  const [
    showConfirmUserDeleteDialogForUser,
    setShowConfirmUserDeleteDialogForUser,
  ] = React.useState<OrganizationUser | null>(null);
  const [showManageRolesDialogForUser, setShowManageRolesDialogForUser] =
    React.useState<OrganizationUser | null>(null);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["User Management", page, rowsPerPage],
    queryFn: () =>
      userManagementService.fetchOrganizationUsers({
        page,
        perPage: rowsPerPage,
      }),
  });

  const { mutateAsync } = useMutation({
    mutationKey: ["set-is-user-active"],
    mutationFn: (payload: SetUserActivePayload) =>
      userManagementService.setIsUserActive(payload),
  });

  const handleSetIsUserActive = async (payload: SetUserActivePayload) => {
    await mutateAsync(payload);
  };

  const { mutateAsync: deleteUserMutateAsync } = useMutation({
    mutationKey: ["delete-user"],
    mutationFn: (userId: string) => userManagementService.deleteUser(userId),
  });

  const handleDeleteUser = async (userId: string) => {
    setShowConfirmUserDeleteDialogForUser(null);
    await deleteUserMutateAsync(userId);
    refetch();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const startTime = Date.now();
      await refetch();

      // Ensure minimum delay of 500ms for better UX
      const elapsedTime = Date.now() - startTime;
      const minDelay = 500;
      if (elapsedTime < minDelay) {
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), minDelay - elapsedTime);
        });
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const rows =
    data?.users?.map((user: OrganizationUser) => {
      return createData(
        user.id,
        user.email,
        user.first_name,
        user.last_name,
        user.is_active,
        formatDateAndTime(user.created_at),
        user.roles,
      );
    }) || [];

  const handleChangePage = (_event: unknown, newPage: number) => {
    if (newPage === 0) {
      setPage(newPage + 1);
    }

    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
  };

  return (
    <>
      {showConfirmUserDeleteDialogForUser && (
        <ConfirmUserDeleteDialog
          user={showConfirmUserDeleteDialogForUser}
          open={!!showConfirmUserDeleteDialogForUser}
          handleClose={() => setShowConfirmUserDeleteDialogForUser(null)}
          handleConfirm={() =>
            handleDeleteUser(showConfirmUserDeleteDialogForUser.id)
          }
        />
      )}
      <Paper
        elevation={2}
        sx={{
          mb: 2,
          mt: { xs: 4, sm: 0 },
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 2 },
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent={{ xs: "center", sm: "flex-end" }}
          sx={{ width: "100%", flexWrap: "nowrap", mb: 0 }}
        >
          <IconButton
            onClick={handleRefresh}
            size="small"
            disabled={isRefreshing}
            sx={{
              color: "primary.main",
              background: "#f5f7fa",
              borderRadius: "50%",
              width: 36,
              height: 36,
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              "&:hover": { background: "#e3e8ef" },
              "&:disabled": {
                background: "#f0f0f0",
                color: "#999",
              },
            }}
          >
            {isRefreshing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <RefreshIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
          <RoleBasedAccess allowedRoles={[RoleName.ADMIN, RoleName.EDITOR]}>
            <ButtonView
              type="button"
              variant="contained"
              size="small"
              label="Add User"
              startIcon={<AddOutlinedIcon sx={{ fontSize: 12 }} />}
              handleOnClick={handleOpenAddUserDialog}
              sx={{
                borderRadius: 999,
                px: 1.5,
                py: 0.5,
                fontSize: 13,
                height: 36,
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                textTransform: "none",
                fontWeight: 600,
                letterSpacing: 0.2,
              }}
            />
          </RoleBasedAccess>
        </Stack>
      </Paper>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1.5, sm: 3 },
          borderRadius: 4,
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          mb: 2,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Loading users...
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 800, overflowX: "auto" }}>
              <TableView
                columns={columns}
                rows={rows}
                handleCustomEvent={async (
                  event: string,
                  value?: string,
                ): Promise<void> => {
                  switch (event) {
                    case "set_is_active": {
                      const selectedUser = data?.users?.find(
                        (user: OrganizationUser) => user.id === value,
                      );
                      if (selectedUser) {
                        await handleSetIsUserActive({
                          id: selectedUser.id,
                          isActive: !selectedUser?.is_active,
                        });
                        refetch();
                      }
                      break;
                    }
                    case "edit_roles": {
                      const selectedUser = data?.users?.find(
                        (user: OrganizationUser) => user.id === value,
                      );
                      if (selectedUser) {
                        setShowManageRolesDialogForUser(selectedUser);
                      }
                      break;
                    }
                    case "delete_user": {
                      const selectedUser = data?.users?.find(
                        (user: OrganizationUser) => user.id === value,
                      );
                      if (selectedUser) {
                        setShowConfirmUserDeleteDialogForUser(selectedUser);
                      }
                      break;
                    }
                    default:
                      break;
                  }
                }}
                handleClick={() => {}}
                TableList={UserRow}
              />
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={data?.pagination.total_items || 0}
              rowsPerPage={rowsPerPage}
              page={page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </Paper>
      {!!showManageRolesDialogForUser && (
        <ManageRolesDialog
          open={!!showManageRolesDialogForUser}
          handleClose={() => setShowManageRolesDialogForUser(null)}
          handleRoleUpdate={refetch}
          user={showManageRolesDialogForUser}
        />
      )}
    </>
  );
}
